import { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Menu, X } from 'lucide-react';
import { SITE } from '@/constants/testIds';
import { BrandMark, Wordmark } from '@/components/site/Logo';
import { QUOTE_MAILTO } from '@/constants/contact';

const links = [
  { label: 'Window Care Services', href: '#services', id: SITE.navLinkServices },
  { label: 'About Us', href: '#about', id: SITE.navLinkManifesto },
  { label: 'Contact', href: '#contact', id: SITE.navLinkContact },
];

export function Nav() {
  const [open, setOpen] = useState(false);

  return (
    <motion.header
      initial={{ y: -30, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      transition={{ duration: 0.9, ease: [0.76, 0, 0.24, 1], delay: 0.1 }}
      className="fixed top-0 inset-x-0 z-50 bg-background/95 backdrop-blur-xl border-b border-border/60 text-foreground"
    >
      <div className="w-full px-5 sm:px-6 lg:px-10 h-16 lg:h-20 flex items-center justify-between gap-4 relative">
        <a
          data-testid={SITE.navBrand}
          href="#top"
          className="group relative flex items-center gap-2.5 shrink-0"
        >
          <BrandMark className="w-9 h-9 lg:w-12 lg:h-12 shrink-0" />
          <Wordmark size="md" />
        </a>

        <nav className="hidden lg:flex items-center gap-10 absolute left-1/2 -translate-x-1/2">
          {links.map((l) => (
            <a
              key={l.href}
              data-testid={l.id}
              href={l.href}
              className="relative text-[13px] tracking-[0.16em] uppercase transition-colors py-2 text-foreground/90 hover:text-foreground"
            >
              <span>{l.label}</span>
            </a>
          ))}
        </nav>

        <div className="flex items-center gap-3 shrink-0">
          <a
            data-testid={SITE.navBookCta}
            href={QUOTE_MAILTO}
            className="hidden md:inline-flex items-center gap-2 px-5 py-3 text-[12px] tracking-[0.22em] uppercase font-medium transition-colors bg-foreground text-background hover:bg-[hsl(var(--gold))] hover:text-foreground"
          >
            Book <span aria-hidden>↗</span>
          </a>
          <button
            data-testid={SITE.navMobileToggle}
            aria-label="Toggle menu"
            onClick={() => setOpen((v) => !v)}
            className="lg:hidden w-11 h-11 flex items-center justify-center border border-border"
          >
            {open ? <X size={18} /> : <Menu size={18} />}
          </button>
        </div>
      </div>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.35 }}
            className="lg:hidden overflow-hidden border-t border-border bg-background/95 backdrop-blur-xl"
          >
            <div className="px-6 py-6 flex flex-col gap-4">
              {links.map((l) => (
                <a
                  key={l.href}
                  href={l.href}
                  onClick={() => setOpen(false)}
                  className="text-[13px] tracking-[0.2em] uppercase text-foreground/95"
                >
                  {l.label}
                </a>
              ))}
              <a
                href="tel:+17204661749"
                onClick={() => setOpen(false)}
                className="text-[13px] tracking-[0.2em] uppercase text-foreground/95"
              >
                720-466-1749
              </a>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.header>
  );
}
